import { FC, useCallback, useMemo } from "react";
import { Row } from "@tanstack/react-table";
import { VirtualItem } from "@tanstack/react-virtual";
import { GapByCountyStatusResult, NetworkPortfolioManagementProject } from "@app/network-portfolio-management/interfaces";
import { MembershipToggleContext, useMembershipToggle } from "@app/network-portfolio-management/hooks/useMembershipToggle";
import { ProjectRunConfigurationType } from "@app/projects/enumerators";
import { VirtualizedTableRow } from "@app/shared/virtualized-table/VirtualizedTableRow";
import { ColumnGroupBoundaries } from "@app/shared/virtualized-table/getColumnGroupBoundaries";
import { features } from "@app/network-portfolio-management/components/project-table/projectTableColumns";

interface ProjectTableRowProps {
  ariaRowIndex: number;
  /** See getColumnGroupBoundaries — draws a vertical divider marking each column group's edges. */
  columnGroupBoundaries?: ColumnGroupBoundaries;
  gapByCountyStatus?: GapByCountyStatusResult;
  gapByCountyStatusHasError: boolean;
  isGapByCountyStatusLoading: boolean;
  membership: ProjectRunConfigurationType;
  onMembershipChange: (projectId: number, value: ProjectRunConfigurationType) => void;
  row: Row<typeof features, NetworkPortfolioManagementProject>;
  virtualRow: VirtualItem;
}

/**
 * Provides MembershipToggleContext once per row so the independently-rendered Dual Membership,
 * Adequacy Index Score, Accuracy Index Score, and Gap Overview cells share one toggle state
 * instead of each calling useMembershipToggle separately.
 *
 * `membership`/`onMembershipChange` come from ProjectTable (keyed by projectId) rather than local
 * state — this row unmounts when it scrolls outside the virtualizer's overscan window, and local
 * useState would reset to the default membership on every remount.
 */
export const ProjectTableRow: FC<ProjectTableRowProps> = ({
  ariaRowIndex,
  columnGroupBoundaries,
  gapByCountyStatus,
  gapByCountyStatusHasError,
  isGapByCountyStatusLoading,
  membership,
  onMembershipChange,
  row,
  virtualRow,
}) => {
  const projectId = row.original.projectId;
  const setMembership = useCallback(
    (value: ProjectRunConfigurationType) => onMembershipChange(projectId, value),
    [onMembershipChange, projectId],
  );
  const membershipToggle = useMembershipToggle(row.original, { membership, setMembership });
  // Same primary/secondary toggle as the card's ProjectCard.tsx applies to its own gapByCountyStatus prop.
  const isSecondarySelected = membershipToggle.showMembershipToggle && membershipToggle.membership === ProjectRunConfigurationType.DualMembershipSupplemental;
  const selectedGapByCountyStatus = isSecondarySelected ? gapByCountyStatus?.secondary : gapByCountyStatus?.primary;
  const gapByCountyStatusErrored = gapByCountyStatusHasError || !!gapByCountyStatus?.hasError;
  const contextValue = useMemo(
    () => ({ ...membershipToggle, gapByCountyStatus: selectedGapByCountyStatus, gapByCountyStatusHasError: gapByCountyStatusErrored, isGapByCountyStatusLoading }),
    [
      membershipToggle.active,
      membershipToggle.membership,
      membershipToggle.setMembership,
      membershipToggle.showMembershipToggle,
      selectedGapByCountyStatus,
      gapByCountyStatusErrored,
      isGapByCountyStatusLoading,
    ],
  );

  return (
    <MembershipToggleContext.Provider value={contextValue}>
      <VirtualizedTableRow
        ariaRowIndex={ariaRowIndex}
        columnGroupBoundaries={columnGroupBoundaries}
        row={row}
        testId={`network-portfolio-management-project-table-row-${projectId}`}
        virtualRow={virtualRow}
      />
    </MembershipToggleContext.Provider>
  );
};
